import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createTransform } from "redux-persist";
import { DisclosureCatalogue } from "JS/Models/Disclosure";

export interface DisclosurePrefetch {
    total: number;
    completed: number;
    fileName?: string;
}

export interface DisclosureState {
    catalogue?: DisclosureCatalogue;
    fingerprint: string;
    playing: boolean;
    prefetch: DisclosurePrefetch;
    // nids whose downloaded rows have already learned their tagging
    backfilled?: string[];
}

const noPrefetch: DisclosurePrefetch = { total: 0, completed: 0 };

const initialState: DisclosureState = {
    catalogue: undefined,
    fingerprint: "",
    playing: false,
    prefetch: noPrefetch,
    backfilled: [],
};

export const disclosureStateSlice = createSlice({
    name: "disclosureState",
    initialState,
    reducers: {
        setDisclosureCatalogue: (
            state,
            data: PayloadAction<{
                catalogue: DisclosureCatalogue;
                fingerprint: string;
            }>,
        ) => ({
            ...state,
            catalogue: data.payload.catalogue,
            fingerprint: data.payload.fingerprint,
        }),

        setDisclosurePlaying: (state, data: PayloadAction<boolean>) => ({
            ...state,
            playing: data.payload,
        }),

        setDisclosurePrefetch: (
            state,
            data: PayloadAction<DisclosurePrefetch>,
        ) => ({
            ...state,
            prefetch: data.payload,
        }),

        clearDisclosurePrefetch: (state) => ({
            ...state,
            prefetch: noPrefetch,
        }),

        recordDisclosureBackfilled: (
            state,
            data: PayloadAction<string[]>,
        ) => ({
            ...state,
            backfilled: Array.from(
                new Set([...(state.backfilled ?? []), ...data.payload]),
            ),
        }),

        resetDisclosureState: () => initialState,
    },
});

// prefetch progress is per-session: persisting it strands a prefetch cut short by a close
export const disclosurePersistTransform = createTransform<
    DisclosureState,
    DisclosureState
>(
    // both are session state: a hard close strands them and playing gates every resume seek
    (inbound) => ({ ...inbound, prefetch: noPrefetch, playing: false }),
    (outbound) => ({ ...outbound, prefetch: noPrefetch, playing: false }),
    { whitelist: ["disclosure"] },
);

export const {
    setDisclosureCatalogue,
    setDisclosurePlaying,
    setDisclosurePrefetch,
    clearDisclosurePrefetch,
    recordDisclosureBackfilled,
    resetDisclosureState,
} = disclosureStateSlice.actions;
